import { PayoutConfigDto } from '../../types/api/location.types';
import {
  LocationPayoutService,
  PayoutConfigResponse,
  PayoutModelType,
  PayoutProviderRule,
  PayoutTier,
} from '../../types/location.types';
import { displayCentsInDollars, isProd } from '../../helpers/helpers';
import {
  TIER_RULE_ALLOWANCE,
  countProviderRules,
  providerRuleAllowance,
  sanitizeProviderRules,
  serviceSupportsProviderRules,
  validateProviderRules,
} from './payoutProviderRules';

/** Display order. Services the API returns that aren't listed get appended. */
export const PAYOUT_SERVICE_ORDER: LocationPayoutService[] = [
  LocationPayoutService.vGrubs,
  LocationPayoutService.vOrders,
  LocationPayoutService.vGrubsAI,
  LocationPayoutService.kanekt365,
  LocationPayoutService.bytes,
];

/** Fallback labels - the API sends `serviceLabel`, this covers seeded configs. */
export const PAYOUT_SERVICE_LABELS: Record<LocationPayoutService, string> = {
  [LocationPayoutService.vGrubs]: 'Bracket Percentage (VRs)',
  [LocationPayoutService.vOrders]: 'vOrders',
  [LocationPayoutService.vGrubsAI]: 'vGrubs AI',
  [LocationPayoutService.kanekt365]: 'Kanekt365',
  [LocationPayoutService.bytes]: 'Bytes.AI',
};

/** Platform defaults, only used to seed a config the API didn't return. */
const PAYOUT_SERVICE_DEFAULT_PERCENTAGE: Record<LocationPayoutService, number> =
  {
    [LocationPayoutService.vGrubs]: 49,
    [LocationPayoutService.vOrders]: 90,
    [LocationPayoutService.vGrubsAI]: 95,
    [LocationPayoutService.kanekt365]: 95,
    [LocationPayoutService.bytes]: 95,
  };

export const PAYOUT_MODEL_OPTIONS: Array<{
  value: PayoutModelType;
  label: string;
}> = [
  { value: PayoutModelType.bracket, label: 'Bracket (percentage)' },
  { value: PayoutModelType.flat_fee, label: 'Flat Fee' },
  { value: PayoutModelType.hybrid, label: 'Hybrid' },
];

export const payoutServiceLabel = (config: PayoutConfigResponse) =>
  config.serviceLabel ||
  PAYOUT_SERVICE_LABELS[config.service] ||
  config.service;

/** vGrubs AI is still hidden on prod, same as the old bracket fields were. */
export const isPayoutServiceVisible = (service: LocationPayoutService) =>
  !(isProd && service === LocationPayoutService.vGrubsAI);

const createDefaultConfig = (
  service: LocationPayoutService
): PayoutConfigResponse => ({
  service,
  serviceLabel: PAYOUT_SERVICE_LABELS[service] ?? service,
  model: PayoutModelType.bracket,
  percentage: PAYOUT_SERVICE_DEFAULT_PERCENTAGE[service] ?? null,
  flatFee: null,
  tiers: [],
  providerRules: [],
});

/**
 * Sorts the API's configs into display order and seeds any service it left
 * out, so the editor always renders a full, stable list.
 */
export const ensurePayoutConfigs = (
  configs: PayoutConfigResponse[] | null | undefined
): PayoutConfigResponse[] => {
  const received = configs ?? [];

  const known = PAYOUT_SERVICE_ORDER.map(
    (service) =>
      received.find((config) => config.service === service) ??
      createDefaultConfig(service)
  );

  const unknown = received.filter(
    (config) => !PAYOUT_SERVICE_ORDER.includes(config.service)
  );

  // `providerRules` is optional on anything cached before the API grew it, and
  // the editor reads both lists without guarding, so they are filled in here.
  // Same for an exception's `sources`, which older stored exceptions predate.
  const withSources = (rules: PayoutProviderRule[] | undefined) =>
    (rules ?? []).map((rule) => ({ ...rule, sources: rule.sources ?? [] }));

  return [...known, ...unknown].map((config) => ({
    ...config,
    tiers: [...(config.tiers ?? [])]
      .sort((a, b) => a.minAmount - b.minAmount)
      .map((tier) => ({
        ...tier,
        providerRules: withSources(tier.providerRules),
      })),
    providerRules: withSources(config.providerRules),
  }));
};

export const createEmptyTier = (tiers: PayoutTier[]): PayoutTier => {
  const last = tiers[tiers.length - 1];

  return {
    // Start where the previous tier ended so the common case needs no editing.
    minAmount: last?.maxAmount ?? 0,
    maxAmount: null,
    percentage: null,
    flatFee: null,
    providerRules: [],
  };
};

const validateTiers = (
  tiers: PayoutTier[],
  label: string,
  /** False for the services whose exceptions are dropped from the payload. */
  providerRulesAllowed: boolean
): string | null => {
  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    const at = `${label} - rule ${i + 1}`;

    if (!Number.isInteger(tier.minAmount) || tier.minAmount < 0)
      return `${at}: "Order Amount From" must be 0 or more.`;

    if (tier.maxAmount != null && tier.maxAmount <= tier.minAmount)
      return `${at}: "Order Amount To" must be greater than "Order Amount From".`;

    if (tier.percentage == null && tier.flatFee == null)
      return `${at}: set a percentage, a flat fee, or both.`;

    if (
      tier.percentage != null &&
      (tier.percentage < 0 || tier.percentage > 100)
    )
      return `${at}: percentage must be between 0 and 100.`;

    if (tier.flatFee != null && tier.flatFee < 0)
      return `${at}: flat fee can't be negative.`;

    if (providerRulesAllowed) {
      const ruleError = validateProviderRules(
        tier.providerRules,
        TIER_RULE_ALLOWANCE,
        at
      );
      if (ruleError) return ruleError;
    }
  }

  // Rules are half-open windows [min, max). Gaps are allowed - a reported
  // amount that hits no rule falls back to the config's own percentage /
  // flat fee.
  const sorted = [...tiers].sort((a, b) => a.minAmount - b.minAmount);

  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].maxAmount == null)
      return `${label}: only the last rule can be left open ended.`;

    if ((sorted[i].maxAmount as number) > sorted[i + 1].minAmount)
      return `${label}: rules must not overlap.`;
  }

  return null;
};

/**
 * Mirrors the backend rules so the admin sees the problem before the request
 * goes out. Returns the first error found, or null when everything is valid.
 * Hidden services are skipped - they aren't part of the payload either.
 */
export const validatePayoutConfigs = (
  configs: PayoutConfigResponse[]
): string | null => {
  for (const config of configs) {
    if (!isPayoutServiceVisible(config.service)) continue;

    const label = payoutServiceLabel(config);

    // Only ever judge the fields this model uses - the others may hold values
    // left over from a model switch, which never reach the payload.
    if (
      config.model !== PayoutModelType.flat_fee &&
      config.percentage != null &&
      !Number.isInteger(config.percentage)
    )
      return `${label}: percentage must be a whole number.`;

    if (config.model === PayoutModelType.bracket) {
      if (config.percentage == null)
        return `${label}: percentage is required for the bracket model.`;

      if (config.percentage < 0 || config.percentage > 100)
        return `${label}: percentage must be between 0 and 100.`;
    }

    if (config.model === PayoutModelType.flat_fee) {
      if (config.flatFee == null)
        return `${label}: flat fee is required for the flat fee model.`;

      if (config.flatFee < 0) return `${label}: flat fee can't be negative.`;
    }

    if (config.model === PayoutModelType.hybrid) {
      if (
        config.percentage == null &&
        config.flatFee == null &&
        !config.tiers.length
      )
        return `${label}: set a percentage, a flat fee, or at least one tier.`;

      if (
        config.percentage != null &&
        (config.percentage < 0 || config.percentage > 100)
      )
        return `${label}: percentage must be between 0 and 100.`;

      if (config.flatFee != null && config.flatFee < 0)
        return `${label}: flat fee can't be negative.`;

      const tierError = validateTiers(
        config.tiers,
        label,
        serviceSupportsProviderRules(config.service)
      );
      if (tierError) return tierError;
    }

    if (serviceSupportsProviderRules(config.service)) {
      const ruleError = validateProviderRules(
        config.providerRules,
        providerRuleAllowance(config.model),
        label
      );
      if (ruleError) return ruleError;
    }
  }

  return null;
};

/** Drops the fields the chosen model doesn't use, so we never send stale values. */
const toPayoutConfigDto = (config: PayoutConfigResponse): PayoutConfigDto => {
  const { service, model } = config;
  const allow = providerRuleAllowance(model);

  /**
   * A write is a full replace, so the list always goes out - an emptied one is
   * how exceptions get removed. Services other than vGrubs reject exceptions
   * outright, hence dropping whatever the state still holds for them.
   */
  const rulesFor = (rules: PayoutProviderRule[]) =>
    serviceSupportsProviderRules(service)
      ? sanitizeProviderRules(rules, allow)
      : [];

  if (model === PayoutModelType.bracket)
    return {
      service,
      model,
      percentage: config.percentage,
      flatFee: null,
      tiers: [],
      providerRules: rulesFor(config.providerRules),
    };

  if (model === PayoutModelType.flat_fee)
    return {
      service,
      model,
      percentage: null,
      flatFee: config.flatFee,
      tiers: [],
      providerRules: rulesFor(config.providerRules),
    };

  return {
    service,
    model,
    percentage: config.percentage,
    flatFee: config.flatFee,
    tiers: config.tiers.map(
      ({ minAmount, maxAmount, percentage, flatFee, providerRules }) => ({
        minAmount,
        maxAmount,
        percentage,
        flatFee,
        providerRules: rulesFor(providerRules),
      })
    ),
    providerRules: rulesFor(config.providerRules),
  };
};

/**
 * Editor state → request payload. Hidden services are left out entirely, which
 * the API reads as "keep the current config".
 */
export const toPayoutConfigsPayload = (
  configs: PayoutConfigResponse[]
): PayoutConfigDto[] =>
  configs
    .filter((config) => isPayoutServiceVisible(config.service))
    .map(toPayoutConfigDto);

/**
 * One-line summary for read-only views, e.g. "95% · $1.50 · 2 rules". Reads
 * only the fields the model actually uses, so values kept in state after a
 * model switch stay out of the summary exactly as they stay out of the payload.
 */
export const describePayoutConfig = (config: PayoutConfigResponse): string => {
  const usesPercentage = config.model !== PayoutModelType.flat_fee;
  const usesFlatFee = config.model !== PayoutModelType.bracket;

  const parts: string[] = [];

  if (usesPercentage && config.percentage != null)
    parts.push(`${config.percentage}%`);

  if (usesFlatFee && config.flatFee != null)
    parts.push(displayCentsInDollars(config.flatFee));

  if (config.model === PayoutModelType.hybrid && config.tiers.length)
    parts.push(
      `${config.tiers.length} rule${config.tiers.length === 1 ? '' : 's'}`
    );

  const exceptions = countProviderRules(config);

  if (exceptions)
    parts.push(`${exceptions} exception${exceptions === 1 ? '' : 's'}`);

  return parts.length ? parts.join(' · ') : '-';
};

export const payoutModelLabel = (model: PayoutModelType) =>
  PAYOUT_MODEL_OPTIONS.find((option) => option.value === model)?.label ?? model;

/** Describes a single tier window, e.g. "$0.00 - $20.00" or "$20.00 and up". */
export const describeTierRange = (tier: PayoutTier): string =>
  tier.maxAmount == null
    ? `${displayCentsInDollars(tier.minAmount)} and up`
    : `${displayCentsInDollars(tier.minAmount)} - ${displayCentsInDollars(
        tier.maxAmount
      )}`;
